import { logger } from "./logger";
import { secureStorage } from "./secureStorage";
/**
 * Geo-detection service for GDPR compliance
 * Identifies if user is from EEA, UK, or Switzerland to determine which CMP to use
 */
// EEA countries (EU member states + Iceland, Liechtenstein, Norway)
// import { logger } from "./logger";
const EEA_COUNTRIES = [
    'AT', // Austria
    'BE', // Belgium
    'BG', // Bulgaria
    'HR', // Croatia
    'CY', // Cyprus
    'CZ', // Czech Republic
    'DK', // Denmark
    'EE', // Estonia
    'FI', // Finland
    'FR', // France
    'DE', // Germany
    'GR', // Greece
    'HU', // Hungary
    'IE', // Ireland
    'IT', // Italy
    'LV', // Latvia
    'LT', // Lithuania
    'LU', // Luxembourg
    'MT', // Malta
    'NL', // Netherlands
    'PL', // Poland
    'PT', // Portugal
    'RO', // Romania
    'SK', // Slovakia
    'SI', // Slovenia
    'ES', // Spain
    'SE', // Sweden
    'IS', // Iceland
    'LI', // Liechtenstein
    'NO', // Norway
];
// Additional GDPR-affected countries
const GDPR_COUNTRIES = [
    ...EEA_COUNTRIES,
    'GB', // United Kingdom
    'CH', // Switzerland
];
export interface GeoLocation {
    countryCode: string | null;
    isGDPRRegion: boolean;
    isEEA: boolean;
    isUK: boolean;
    isSwitzerland: boolean;
    detectionMethod: 'api' | 'timezone' | 'cached' | 'unknown';
}
const GEO_CACHE_KEY = 'apt_geo_location';
const GEO_CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours
/**
 * Get cached geo location if available and not expired
 */
async function getCachedGeoLocation(): Promise<GeoLocation | null> {
    try {
        const cached = await secureStorage.getEncrypted<{ data: GeoLocation; timestamp: number }>(
            GEO_CACHE_KEY,
        );
        if (!cached)
            return null;
        const { data, timestamp } = cached;
        const now = Date.now();
        // Check if cache is still valid (24 hours)
        if (now - timestamp < GEO_CACHE_DURATION) {
            return {
                ...data,
                detectionMethod: 'cached' as const
            };
        }
        // Cache expired, remove it
        await secureStorage.remove(GEO_CACHE_KEY);
    }
    catch (error) {
        logger.error('Failed to read geo location cache:', error, {});
    }
    return null;
}
/**
 * Cache geo location for future use
 */
async function cacheGeoLocation(location: GeoLocation): Promise<void> {
    try {
        const cacheData = {
            data: location,
            timestamp: Date.now()
        };
        await secureStorage.setEncrypted(GEO_CACHE_KEY, cacheData); // Encrypt location data for privacy
    }
    catch (error) {
        logger.error('Failed to cache geo location:', error, {});
    }
}
/**
 * Detect user location via cloudflare trace (fast and free)
 */
async function detectViaCloudflare(): Promise<string | null> {
    try {
        const response = await fetch('https://www.cloudflare.com/cdn-cgi/trace', {
            method: 'GET',
            cache: 'no-cache'
        });
        if (!response.ok)
            return null;
        const text = await response.text();
        const lines = text.split('\n');
        const locLine = lines.find(line => line.startsWith('loc='));
        if (locLine) {
            const countryCode = locLine.split('=')[1];
            return countryCode.toUpperCase();
        }
    }
    catch (error) {
        logger.error('Cloudflare geo detection failed:', error, {});
    }
    return null;
}
/**
 * Fallback: Estimate region based on timezone (less accurate)
 */
function estimateFromTimezone(): GeoLocation {
    try {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        // European timezones that might indicate GDPR region
        const europeanTimezones = [
            'Europe/',
            'Atlantic/Reykjavik', // Iceland
            'Atlantic/Faroe', // Denmark
            'Arctic/Longyearbyen', // Norway
        ];
        const isLikelyEurope = europeanTimezones.some(tz => timezone.startsWith(tz));
        // Conservative approach: if we think they might be in Europe, treat as GDPR region
        return {
            countryCode: null,
            isGDPRRegion: isLikelyEurope,
            isEEA: isLikelyEurope,
            isUK: false,
            isSwitzerland: false,
            detectionMethod: 'timezone'
        };
    }
    catch (error) {
        logger.error('Timezone detection failed:', error, {});
    }
    // Ultimate fallback: assume non-GDPR to avoid unnecessary restrictions
    return {
        countryCode: null,
        isGDPRRegion: false,
        isEEA: false,
        isUK: false,
        isSwitzerland: false,
        detectionMethod: 'unknown'
    };
}
/**
 * Create GeoLocation object from country code
 */
function createGeoLocation(countryCode: string | null, method: 'api' | 'timezone'): GeoLocation {
    if (!countryCode) {
        return estimateFromTimezone();
    }
    const upperCode = countryCode.toUpperCase();
    const isEEA = EEA_COUNTRIES.includes(upperCode);
    const isUK = upperCode === 'GB';
    const isSwitzerland = upperCode === 'CH';
    const isGDPRRegion = GDPR_COUNTRIES.includes(upperCode);
    return {
        countryCode: upperCode,
        isGDPRRegion,
        isEEA,
        isUK,
        isSwitzerland,
        detectionMethod: method
    };
}
/**
 * Detect user's geographic location
 * Returns information about whether user is in a GDPR-regulated region
 */
export async function detectUserLocation(): Promise<GeoLocation> {
    // Check cache first
    const cached = await getCachedGeoLocation();
    if (cached) {
        if (process.env.NODE_ENV === 'development') {
            logger.debug('🌍 Geo location (cached):', { "arg0": cached });
        }
        return cached;
    }
    // Try Cloudflare detection
    const countryCode = await detectViaCloudflare();
    const location = createGeoLocation(countryCode, 'api');
    // Cache the result
    await cacheGeoLocation(location);
    if (process.env.NODE_ENV === 'development') {
        logger.debug('🌍 Geo location detected:', { "arg0": location });
    }
    return location;
}
/**
 * Check if user requires IAB TCF-certified CMP
 * (EEA, UK, or Switzerland users)
 */
export async function requiresCertifiedCMP(): Promise<boolean> {
    const location = await detectUserLocation();
    return location.isGDPRRegion;
}
/**
 * Clear cached geo location (useful for testing)
 */
export function clearGeoCache(): void {
    try {
        localStorage.removeItem(GEO_CACHE_KEY);
        logger.debug('Geo location cache cleared');
    }
    catch (error) {
        logger.error('Failed to clear geo cache:', error, {});
    }
}
// Expose cache clearing function in development
if (process.env.NODE_ENV === 'development') {
    (window as any).clearGeoCache = clearGeoCache;
}
